import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import to from 'await-to-js'

import {
  PiArrowsClockwise,
  PiCalendarBlank,
  PiCaretLeft,
  PiCaretRight,
  PiEnvelopeSimple,
  PiFloppyDisk,
  PiPlus,
  PiTrash,
  PiUser,
} from 'react-icons/pi'

import { Alert } from 'components/FbUI/Alert'
import FbButton from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import TimePicker from 'components/FormUtils/TimePicker'
import { handleFormError } from 'utils/handleFormError'

import { WORKFLOW_STATUS } from 'models/campaign'
import { colors } from 'styles/tokens'
import { LiveCampaignBanner } from './LiveCampaignBanner'
import { WizardStepFooter, WIZARD_BUTTON_MIN_W } from './WizardStepFooter'
import { WizardStepHeader } from './WizardStepHeader'
import {
  useGetCampaignLaunchReadiness,
  useGetCampaignSchedule,
  useUpdateCampaignSchedule,
} from 'services/campaigns'
import { useSyncCampaignLeads } from '../leads_api'
import {
  useGetCampaignSenderEmails,
  useSyncWorkspaceSenderEmails,
  useDetachCampaignSenderEmail,
  type SenderEmail,
} from '../sender_emails_api'
import { useGetCampaignSequence } from '../sequences_api'
import type {
  SequenceSpecSchedule,
  SequenceSpecSenderEmail,
} from '../sequence_spec_api'
import { AddSenderEmailsModal } from '../SequenceTab/AddSenderEmailsModal'
import { TIMEZONE_OPTIONS } from '../SequenceTab/timezones'

interface WizardSettingsStepProps {
  campaignId: number
  onNext: () => void
  onBack: () => void
  hasContacts: boolean
  stepNumber: number
  totalSteps: number
  onSaveExit?: () => void
  isLiveCampaign?: boolean
  readOnly?: boolean
  campaignStatus?: string
  /**
   * When copying from an existing campaign (FIR-7659), the source's schedule.
   * Prefilled only while this campaign has no schedule of its own; the user can
   * edit it before continuing. Null for the blank flow.
   */
  sourceSchedule?: SequenceSpecSchedule | null
  /**
   * Source senders that were dropped because they are no longer connected —
   * surfaced so the user knows to attach a replacement.
   */
  droppedSenders?: SequenceSpecSenderEmail[]
}

interface WizardSectionHandle {
  submitIfDirty: () => Promise<boolean>
}

interface CampaignScheduleWizardForm {
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
  start_time: string
  end_time: string
  timezone: string
}

const SCHEDULE_DEFAULT_VALUES: CampaignScheduleWizardForm = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
  start_time: '08:00',
  end_time: '17:00',
  timezone: 'America/New_York',
}

const DAYS: Array<{
  key: keyof Pick<
    CampaignScheduleWizardForm,
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday'
  >
  label: string
}> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
]

const SCHEDULE_PRESETS: Array<{
  label: string
  description: string
  values: CampaignScheduleWizardForm
}> = [
  {
    label: 'Business Hours',
    description: 'Mon-Fri, 8am-5pm',
    values: {
      ...SCHEDULE_DEFAULT_VALUES,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
      start_time: '08:00',
      end_time: '17:00',
    },
  },
  {
    label: 'Morning Focus',
    description: 'Mon-Fri, 7am-11am',
    values: {
      ...SCHEDULE_DEFAULT_VALUES,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
      start_time: '07:00',
      end_time: '11:00',
    },
  },
  {
    label: 'Extended Hours',
    description: 'Mon-Sat, 7am-7pm',
    values: {
      ...SCHEDULE_DEFAULT_VALUES,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: false,
      start_time: '07:00',
      end_time: '19:00',
    },
  },
  {
    label: 'Afternoons Only',
    description: 'Mon-Fri, 12pm-5pm',
    values: {
      ...SCHEDULE_DEFAULT_VALUES,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
      start_time: '12:00',
      end_time: '17:00',
    },
  },
]

function toTimePickerFormat(time: string | undefined): string {
  if (!time) return '08:00'
  if (/^\d{2}:\d{2}$/.test(time)) return time
  const match = time.match(/^(\d{2}:\d{2})/)
  return match ? match[1] : '08:00'
}

/**
 * Map a schedule-shaped record (server schedule or a copied source schedule,
 * which share the same fields) into the schedule form's values, normalizing the
 * stored HH:MM:SS times to the time picker's HH:MM format.
 */
function toScheduleFormValues(
  schedule: SequenceSpecSchedule
): CampaignScheduleWizardForm {
  return {
    monday: schedule.monday,
    tuesday: schedule.tuesday,
    wednesday: schedule.wednesday,
    thursday: schedule.thursday,
    friday: schedule.friday,
    saturday: schedule.saturday,
    sunday: schedule.sunday,
    start_time: toTimePickerFormat(schedule.start_time),
    end_time: toTimePickerFormat(schedule.end_time),
    timezone: schedule.timezone,
  }
}

function getSenderStatusLabel(status: string): string {
  switch (status) {
    case 'connected':
      return 'Connected'
    case 'not_connected':
      return 'Not Connected'
    case 'pending_move':
      return 'Pending Move'
    case 'pending_deletion':
      return 'Pending Deletion'
    default:
      return status
  }
}

function getSenderStatusClassName(status: string): string {
  switch (status) {
    case 'connected':
      return 'bg-success-bg-subtle text-success-strong'
    case 'pending_move':
      return 'bg-warning-bg-subtle text-warning-strong'
    case 'pending_deletion':
      return 'bg-error-bg-subtle text-error-strong'
    default:
      return 'bg-surface-alt text-fg-secondary'
  }
}

function getSenderInitials(sender: SenderEmail): string {
  if (sender.name?.trim()) {
    const parts = sender.name.trim().split(/\s+/)
    return parts
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('')
  }
  return sender.email.slice(0, 2).toUpperCase()
}

export function WizardSettingsStep({
  campaignId,
  onNext,
  onBack,
  hasContacts,
  stepNumber,
  totalSteps,
  onSaveExit,
  isLiveCampaign = false,
  readOnly = false,
  campaignStatus,
  sourceSchedule = null,
  droppedSenders,
}: WizardSettingsStepProps) {
  const scheduleRef = useRef<WizardSectionHandle | null>(null)
  const { data: sequence } = useGetCampaignSequence(campaignId)
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0
  const { refetch: refetchReadiness } = useGetCampaignLaunchReadiness(
    campaignId,
    hasContacts
  )
  const syncCampaignLeadsMutation = useSyncCampaignLeads(
    bisonCampaignId,
    campaignId
  )

  const saveDirtySections = async () => {
    return scheduleRef.current?.submitIfDirty() ?? true
  }

  const handleContinue = async () => {
    const saveSucceeded = await saveDirtySections()
    if (!saveSucceeded) return

    if (hasContacts && bisonCampaignId > 0) {
      const [syncErr] = await to(syncCampaignLeadsMutation.mutateAsync())
      if (syncErr) {
        toast.error('Sync contacts before continuing to launch')
        return
      }
    }

    const readinessResult = await refetchReadiness()
    const readiness = readinessResult.data

    if (!readiness?.sender_emails.is_complete) {
      const disconnected = readiness?.sender_emails.disconnected_emails ?? []
      toast.error(
        disconnected.length > 0
          ? `Reconnect mailbox(es) before continuing: ${disconnected.join(', ')}`
          : 'Attach at least one sender email before continuing'
      )
      return
    }

    if (!readiness?.schedule.is_complete) {
      toast.error('Create and save a sending schedule before continuing')
      return
    }

    onNext()
  }

  const handleSaveExit = async () => {
    const saveSucceeded = await saveDirtySections()
    if (!saveSucceeded) return
    onSaveExit?.()
  }

  return (
    <div className="flex h-full flex-col">
      <WizardStepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title="Campaign Settings"
        description="Choose who sends this campaign and when emails are allowed to go out."
      />

      <div className="flex-1 overflow-auto rounded-bl-2xl border-b border-l border-line bg-surface-bright px-8 pt-6">
        <div className="mx-auto max-w-[1100px] space-y-6 pb-6">
          {readOnly && (
            <Alert
              color="warning"
              description="This campaign is active. Pause the campaign to edit settings."
            />
          )}
          {isLiveCampaign && !readOnly && <LiveCampaignBanner />}
          <fieldset
            disabled={readOnly}
            className={cn(readOnly && 'opacity-60', 'space-y-6')}
          >
            <SettingsSection
              icon={PiUser}
              title="Sender & Domain"
              description="Each sender is tied to a sending domain. Pick which connected accounts can send this campaign."
            >
              <WizardSenderDomainSection
                campaignId={campaignId}
                campaignStatus={campaignStatus}
                droppedSenders={droppedSenders}
              />
            </SettingsSection>

            <SettingsSection
              icon={PiCalendarBlank}
              title="Send Schedule"
              description="Choose which days and hours emails are sent, with presets to get started quickly."
            >
              <WizardScheduleSection
                ref={scheduleRef}
                campaignId={campaignId}
                sourceSchedule={sourceSchedule}
              />
            </SettingsSection>
          </fieldset>
        </div>

        {/* Pinned to the bottom of the card, above the footer (matching the
            validation-banner pattern on the sequence step) — full-width with a
            top border instead of an inline rounded card. */}
        <div className="sticky bottom-0 z-10 -mx-8 border-t border-alert-default-border bg-alert-default-bg px-8 py-3 text-sm text-alert-default-title">
          <p>
            Email signatures are managed in{' '}
            <Link
              to="/settings/outreach/email-outreach"
              className="font-medium text-brand-active transition-colors hover:text-basil-800"
              target="_blank"
              rel="noreferrer"
            >
              Email Outreach settings
            </Link>
            . Reply opt-out detection is managed in{' '}
            <Link
              to="/settings/outreach/sequencing"
              className="font-medium text-brand-active transition-colors hover:text-basil-800"
              target="_blank"
              rel="noreferrer"
            >
              Sequencing settings
            </Link>
            . Changes apply to all campaigns.
          </p>
        </div>
      </div>

      <WizardStepFooter
        subtext={
          !hasContacts
            ? 'Add contacts from the campaign page to unlock Contacts and Launch steps.'
            : undefined
        }
      >
        <FbButton
          type="button"
          variant="outline"
          onClick={onBack}
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
        >
          <PiCaretLeft className="size-4" />
          Back
        </FbButton>
        {hasContacts && !isLiveCampaign && !readOnly ? (
          <FbButton
            type="button"
            onClick={handleContinue}
            loading={syncCampaignLeadsMutation.isPending}
            disabled={syncCampaignLeadsMutation.isPending}
            className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
          >
            Continue to Review
            <PiCaretRight className="size-4" />
          </FbButton>
        ) : (
          <FbButton
            type="button"
            onClick={handleSaveExit}
            className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
          >
            <PiFloppyDisk className="size-4" />
            Save & Exit
          </FbButton>
        )}
      </WizardStepFooter>
    </div>
  )
}

function WizardSenderDomainSection({
  campaignId,
  campaignStatus,
  droppedSenders,
}: {
  campaignId: number
  campaignStatus?: string
  droppedSenders?: SequenceSpecSenderEmail[]
}) {
  const isSendingActive = campaignStatus === WORKFLOW_STATUS.ACTIVE
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const { data: sequence } = useGetCampaignSequence(campaignId)
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0
  const {
    data: senderEmails,
    isLoading,
    isFetching,
  } = useGetCampaignSenderEmails(bisonCampaignId)
  const syncMutation = useSyncWorkspaceSenderEmails()
  const detachMutation = useDetachCampaignSenderEmail(bisonCampaignId)

  const attachedSenderEmails = senderEmails?.results ?? []
  // The copied source's connected senders are pre-attached at the wizard-page
  // level when the campaign is provisioned (FIR-7659), so by the time this
  // section mounts they are already attached — no attach-on-mount flicker.

  const handleSync = async () => {
    const [err, result] = await to(syncMutation.mutateAsync())
    if (err) {
      toast.error('Failed to sync sender emails')
      return
    }
    toast.success(`Synced: ${result.created} added, ${result.updated} updated`)
  }

  const handleRemove = async (senderEmailId: number) => {
    const [err] = await to(detachMutation.mutateAsync(senderEmailId))
    if (err) {
      toast.error('Failed to remove sender email')
      return
    }
    toast.success('Sender email removed from campaign')
  }

  if (!bisonCampaignId) {
    return (
      <div className="rounded-xl border border-dashed border-line bg-surface-alt px-5 py-6 text-sm text-fg-secondary">
        Create and save a sequence first, then come back to attach sender
        emails.
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        {droppedSenders && droppedSenders.length > 0 && (
          <Alert
            color="warning"
            description={`${droppedSenders.length} sender email${
              droppedSenders.length === 1 ? '' : 's'
            } from the copied campaign ${
              droppedSenders.length === 1 ? 'is' : 'are'
            } no longer connected and ${
              droppedSenders.length === 1 ? 'was' : 'were'
            } skipped: ${droppedSenders
              .map((sender) => sender.email)
              .join(', ')}. Attach a sender below.`}
          />
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-fg-secondary">
            {attachedSenderEmails.length > 0
              ? `${attachedSenderEmails.length} sender email${attachedSenderEmails.length === 1 ? '' : 's'} attached`
              : 'No sender emails attached yet'}
          </div>
          <div className="flex flex-wrap gap-2">
            <FbButton
              variant="secondary"
              size="sm"
              onClick={handleSync}
              disabled={syncMutation.isPending}
              loading={syncMutation.isPending}
              className="gap-1.5"
            >
              <PiArrowsClockwise className="size-3.5" />
              Sync Workspace
            </FbButton>
            <FbButton
              variant="primary"
              size="sm"
              onClick={() => setIsAddModalOpen(true)}
              className="gap-1.5"
            >
              <PiPlus className="size-3.5" />
              Add Sender Emails
            </FbButton>
          </div>
        </div>

        {isLoading || isFetching ? (
          <div className="rounded-xl border border-line bg-surface-alt px-5 py-8 text-center text-sm text-fg-secondary">
            Loading sender emails...
          </div>
        ) : attachedSenderEmails.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-neutral-50 px-5 py-8">
            <div className="mx-auto max-w-lg text-center">
              <div className="mx-auto mb-3 flex size-11 items-center justify-center rounded-xl bg-brand/10">
                <PiEnvelopeSimple className="size-5 text-brand" />
              </div>
              <h3 className="text-sm font-semibold text-fg">
                Choose one or more sender emails
              </h3>
              <p className="mt-1 text-sm text-fg-secondary">
                Attached sender accounts determine which inboxes and domains can
                send this campaign.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Alert
              color="success"
              description={
                <>
                  Emails will send from{' '}
                  <span className="font-medium">
                    {attachedSenderEmails.length === 1
                      ? attachedSenderEmails[0].email
                      : `${attachedSenderEmails.length} connected sender emails`}
                  </span>
                  .
                </>
              }
            />

            <div className="space-y-2">
              {attachedSenderEmails.map((senderEmail) => (
                <div
                  key={senderEmail.id}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-white px-4 py-3"
                >
                  <div className="flex size-9 items-center justify-center rounded-full bg-brand-sage-100 text-xs font-semibold text-brand-sage-700">
                    {getSenderInitials(senderEmail)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-medium text-fg">
                        {senderEmail.name || senderEmail.email}
                      </p>
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2 py-0.5 text-xs',
                          getSenderStatusClassName(senderEmail.status)
                        )}
                      >
                        {getSenderStatusLabel(senderEmail.status)}
                      </span>
                    </div>
                    <p className="truncate text-sm text-fg-secondary">
                      {senderEmail.email}
                    </p>
                  </div>
                  <div className="text-right text-sm text-fg-secondary">
                    <p>Daily limit</p>
                    <p className="font-medium text-fg">
                      {senderEmail.daily_limit}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(senderEmail.id)}
                    disabled={detachMutation.isPending || isSendingActive}
                    className="flex size-8 items-center justify-center rounded-lg text-fg-secondary transition-colors hover:bg-error-bg hover:text-error disabled:cursor-not-allowed disabled:opacity-50"
                    title={
                      isSendingActive
                        ? 'Cannot remove sender emails while campaign is actively sending. Pause the campaign first.'
                        : 'Remove sender email'
                    }
                  >
                    <PiTrash className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <AddSenderEmailsModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        bisonCampaignId={bisonCampaignId}
      />
    </>
  )
}

const WizardScheduleSection = forwardRef<
  WizardSectionHandle,
  { campaignId: number; sourceSchedule?: SequenceSpecSchedule | null }
>(function WizardScheduleSection({ campaignId, sourceSchedule = null }, ref) {
  const { data: schedule, isLoading } = useGetCampaignSchedule(campaignId)
  const updateScheduleMutation = useUpdateCampaignSchedule()

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    setError,
    setValue,
    watch,
  } = useForm<CampaignScheduleWizardForm>({
    defaultValues: SCHEDULE_DEFAULT_VALUES,
    mode: 'onChange',
  })

  const values = watch()

  // Apply the copied source schedule at most once. Without this guard, a
  // sourceSchedule identity change (e.g. a React Query refetch of the source
  // spec) would re-run the prefill reset and silently wipe edits the user made
  // to the prefilled schedule before saving (FIR-7659).
  const sourceScheduleAppliedRef = useRef(false)
  useEffect(() => {
    if (schedule) {
      reset(toScheduleFormValues(schedule))
      return
    }
    // No server schedule yet. Prefill once from a copied source schedule when
    // provided; otherwise fall back to defaults. The form stays editable and
    // submitIfDirty force-saves it on continue.
    if (sourceScheduleAppliedRef.current) return
    if (sourceSchedule) {
      sourceScheduleAppliedRef.current = true
      reset(toScheduleFormValues(sourceSchedule))
      return
    }
    reset(SCHEDULE_DEFAULT_VALUES)
  }, [reset, schedule, sourceSchedule])

  const activePreset = useMemo(() => {
    return SCHEDULE_PRESETS.find((preset) => {
      return (
        DAYS.every((day) => preset.values[day.key] === values[day.key]) &&
        preset.values.start_time === values.start_time &&
        preset.values.end_time === values.end_time
      )
    })?.label
  }, [values])

  const persistValues = useCallback(
    async (nextValues: CampaignScheduleWizardForm): Promise<boolean> => {
      const [err] = await to(
        updateScheduleMutation.mutateAsync({
          campaign_id: campaignId,
          data: nextValues,
        })
      )
      if (err) {
        handleFormError(err, setError)
        toast.error('Failed to save schedule')
        return false
      }
      reset(nextValues)
      return true
    },
    [reset, setError, updateScheduleMutation]
  )

  const submitIfDirty = useCallback(async (): Promise<boolean> => {
    // Force save if no schedule has been created yet on Bison,
    // even if the form isn't "dirty" (default values were never persisted)
    if (!isDirty && schedule) return true

    let success = false
    await handleSubmit(async (nextValues) => {
      success = await persistValues(nextValues)
    })()
    return success
  }, [handleSubmit, isDirty, persistValues, schedule])

  useImperativeHandle(ref, () => ({ submitIfDirty }), [submitIfDirty])

  if (isLoading) {
    return (
      <div className="py-8 text-sm text-fg-secondary">Loading schedule...</div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="mb-2 block text-sm font-medium text-fg-secondary">
          Quick presets
        </label>
        <div className="grid gap-2 md:grid-cols-2">
          {SCHEDULE_PRESETS.map((preset) => {
            const isActive = activePreset === preset.label
            return (
              <label
                key={preset.label}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition-all',
                  isActive
                    ? 'border-brand-sage-300 bg-brand-sage-50 ring-2 ring-brand-sage-200'
                    : 'border-line hover:border-brand-sage-300'
                )}
              >
                <input
                  type="radio"
                  name="schedule-preset"
                  checked={isActive}
                  onChange={() => {
                    DAYS.forEach((day) => {
                      setValue(day.key, preset.values[day.key], {
                        shouldDirty: true,
                      })
                    })
                    setValue('start_time', preset.values.start_time, {
                      shouldDirty: true,
                    })
                    setValue('end_time', preset.values.end_time, {
                      shouldDirty: true,
                    })
                  }}
                  className="mt-1 accent-brand"
                />
                <div className="flex-1">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      isActive ? 'text-brand-sage-700' : 'text-fg'
                    )}
                  >
                    {preset.label}
                  </p>
                  <p className="mt-0.5 text-sm text-fg-secondary">
                    {preset.description}
                  </p>
                </div>
              </label>
            )
          })}
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-fg-secondary">
          Send on these days
        </label>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((day) => {
            const isSelected = values[day.key]
            return (
              <button
                key={day.key}
                type="button"
                onClick={() =>
                  setValue(day.key, !isSelected, { shouldDirty: true })
                }
                className={cn(
                  'flex h-10 w-12 items-center justify-center rounded-lg text-sm transition-colors',
                  isSelected
                    ? 'bg-brand-active text-white'
                    : 'bg-neutral-100 text-fg-secondary hover:bg-neutral-200'
                )}
              >
                {day.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr,1fr,240px]">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-fg-secondary">
            Start time
          </label>
          <Controller
            name="start_time"
            control={control}
            render={({ field }) => (
              <TimePicker
                value={field.value}
                onChange={(value) => field.onChange(value)}
                className="w-full"
              />
            )}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-fg-secondary">
            End time
          </label>
          <Controller
            name="end_time"
            control={control}
            render={({ field }) => (
              <TimePicker
                value={field.value}
                onChange={(value) => field.onChange(value)}
                className="w-full"
              />
            )}
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1.5 block text-sm font-medium text-fg-secondary">
            Timezone
          </label>
          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <ReactSelectWrapper
                inputId="campaign-schedule-timezone"
                instanceId="campaign-schedule-timezone"
                menuPosition="fixed"
                isClearable={false}
                options={TIMEZONE_OPTIONS}
                value={
                  TIMEZONE_OPTIONS.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                onChange={(newValue: any) =>
                  field.onChange(newValue?.value ?? '')
                }
                placeholder="Select timezone"
                styles={{
                  // react-select's default focus styling is blue; override
                  // border + ring to the sage `line-focus` token so the
                  // control matches the rest of the app's form fields.
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: 'unset',
                    height: 'calc(1.5em + 0.75rem + 2px)',
                    borderColor: state.isFocused
                      ? colors['line-focus']
                      : colors['neutral-300'],
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: state.isFocused
                        ? colors['line-focus']
                        : colors['neutral-300'],
                    },
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: 'calc(1.5em + 0.75rem)',
                    padding: '0 8px',
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: '0',
                    padding: '0',
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: 'calc(1.5em + 0.75rem)',
                  }),
                  // DS2026 dropdown menu (Figma node 7215:85370): rounded-xl
                  // panel, soft shadow, 6px list padding, rounded-lg options.
                  menu: (provided) => ({
                    ...provided,
                    borderRadius: 12,
                    border: `1px solid ${colors['neutral-200']}`,
                    boxShadow:
                      '0px 2px 4px -2px rgba(16,24,40,0.06), 0px 4px 8px -2px rgba(16,24,40,0.10)',
                    overflow: 'hidden',
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    padding: 6,
                  }),
                  // ReactSelectWrapper forces the global green-tint
                  // .hover-menu-item / .selected-menu-item classes (declared
                  // with !important in main.css), so the focused/selected
                  // colors here must also use !important to win.
                  option: (provided, state) => ({
                    ...provided,
                    padding: '10px 12px',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                    color:
                      state.isFocused || state.isSelected
                        ? `${colors['neutral-800']} !important`
                        : `${colors['neutral-700']} !important`,
                    backgroundColor:
                      state.isFocused || state.isSelected
                        ? `${colors['neutral-100']} !important`
                        : 'transparent !important',
                  }),
                }}
              />
            )}
          />
        </div>
      </div>

      <Alert
        color="default"
        description="Wait times between steps are counted in calendar days. Emails are only scheduled on the active days within this timezone window, and per-sender daily limits still apply on top of it."
      />
    </div>
  )
})

interface SettingsSectionProps {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  children: React.ReactNode
}

function SettingsSection({
  icon: Icon,
  title,
  description,
  children,
}: SettingsSectionProps) {
  return (
    <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex size-9 items-center justify-center rounded-lg bg-neutral-100">
          <Icon className="size-4 text-fg-secondary" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-fg">{title}</h3>
          <p className="text-sm text-fg-secondary">{description}</p>
        </div>
      </div>
      {children}
    </div>
  )
}
